/**
 * „Meine Anrufliste"-Kachel (Jul-2026, User-Wunsch).
 *
 * Zeigt jedem Verkäufer morgens seine Top-10-Anrufliste direkt auf dem
 * Dashboard: überfällige Wiedervorlagen zuerst, dann heute fällige, dann
 * frische heiße Leads — sortiert nach Lead-Score (A/B/C).
 *
 * Datenquelle: ``GET /api/dealer/leads/call-list`` (mine=1 für Mitarbeiter).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, ArrowRight, RefreshCw, PhoneCall } from "lucide-react";
import api from "../../lib/api";

const BUCKET = {
    0: { label: "WV überfällig", cls: "bg-red-100 text-red-800 border-red-300" },
    1: { label: "WV heute", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    2: { label: "HEISS · < 3 Std.", cls: "bg-red-600 text-white border-red-600" },
};

// Ampel-Logik (User-Wunsch): guter Score = grün, mittel = gelb, schlecht = rot
const gradeCls = (g) => (g === "A" ? "bg-emerald-600 text-white border-emerald-600"
    : g === "B" ? "bg-amber-400 text-[#111827] border-amber-400"
    : "bg-red-600 text-white border-red-600");

// Aug-2026 (User-Wunsch) — nach „Anrufen“ sofort das Anruf-Ergebnis erfassen
const OUTCOMES = [
    ["not_reached", "Nicht erreicht"],
    ["no_response", "Keine Rückmeldung"],
    ["just_asking", "Wollte nur mal nachfragen"],
    ["will_come_back", "Meldet sich wieder"],
    ["test_ride", "Probefahrt vereinbart"],
];

export default function CallListCard({ user }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [assigning, setAssigning] = useState("");
    const [outcomeFor, setOutcomeFor] = useState("");
    // Verkaufsleiter/Filialleiter sehen die komplette Liste + Zuweisung; nur
    // reine Verkäufer bekommen ausschließlich ihre eigenen Leads (mine=1).
    const isEmp = user?.role === "zweiradverkaeufer" && (user?.employee_function || "verkaeufer") === "verkaeufer";

    const load = () => {
        setBusy(true);
        api.get("/dealer/leads/call-list", { params: isEmp ? { mine: 1 } : {} })
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setBusy(false));
    };
    useEffect(() => {
        if (!user) return;
        load();
        // Jul-2026 (User-Wunsch) — Mitarbeiter direkt aus der Anrufliste zuweisen
        if (!isEmp) api.get("/me/tasks/assignable-employees").then((r) => setEmployees(r.data.items || [])).catch(() => {});
    }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const assignSeller = async (item, sellerId) => {
        setAssigning(item.id);
        try {
            const r = await api.patch(`/dealer/leads/${item.id}/assign`, { seller_id: sellerId || null });
            setData((p) => (p ? {
                ...p,
                items: p.items.map((x) => (x.id === item.id
                    ? { ...x, assigned_seller_id: r.data.assigned_seller_id, assigned_seller_name: r.data.assigned_seller_name }
                    : x)),
            } : p));
            toast.success(sellerId ? `Zugewiesen an ${r.data.assigned_seller_name}` : "Zuweisung aufgehoben");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zuweisung fehlgeschlagen");
        }
        setAssigning("");
    };

    const saveOutcome = async (item, k) => {
        setOutcomeFor("");
        try {
            const r = await api.patch(`/dealer/leads/${item.id}/outcome`, { outcome: k });
            toast.success(`Ereignis erfasst: ${r.data.outcome_label || k}`);
            load(); // Erfolgsquote sofort aktualisieren
        } catch (e) {
            toast.error(e.response?.data?.detail || "Ereignis konnte nicht gespeichert werden");
        }
    };

    if (!user || !data || !(data.items || []).length) return null;
    const items = data.items.slice(0, 10);

    return (
        <div className="bg-white border-2 border-red-200 rounded-sm p-4 mb-6" data-testid="call-list-card">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <PhoneCall className="w-4 h-4 text-red-600" />
                    <h3 className="font-display font-black text-base text-[#111827]">
                        {isEmp ? "Meine Anrufliste — heute" : "Anrufliste — heute"}
                    </h3>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-100 rounded-sm px-1.5 py-0.5" data-testid="call-list-total">
                        {data.total_open} offen
                    </span>
                    {/* Aug-2026 (User-Wunsch) — Tages-Erfolgsquote als Motivation */}
                    {data.today_quote?.attempts > 0 && (
                        <span
                            className={`text-[10px] font-bold uppercase tracking-wider rounded-sm px-1.5 py-0.5 border ${data.today_quote.pct >= 50 ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-amber-50 text-amber-800 border-amber-300"}`}
                            title="Heute erfasste Anruf-Ergebnisse — erreicht = Kunde gesprochen (Nachfrage, meldet sich, Probefahrt)"
                            data-testid="call-list-quote"
                        >
                            Heute: {data.today_quote.reached}/{data.today_quote.attempts} erreicht ({data.today_quote.pct}%)
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={load} disabled={busy} className="text-xs font-bold text-gray-500 hover:text-[#111827] inline-flex items-center gap-1 disabled:opacity-50" data-testid="call-list-refresh">
                        <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Aktualisieren
                    </button>
                    <Link to="/dealer/leads" className="text-xs font-bold text-[#111827] inline-flex items-center gap-1 hover:underline" data-testid="call-list-all-link">
                        Alle Leads <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
            </div>
            <ol className="divide-y divide-gray-100" data-testid="call-list-items">
                {items.map((l, i) => {
                    const si = l.score_info || {};
                    const b = BUCKET[l.call_bucket];
                    const phone = (l.seller?.phone || "").trim();
                    const phoneWrong = !!(l.contact_flags || {}).phone_wrong;
                    const name = l.seller?.name || l.subject || "Interessent";
                    return (
                        <li key={l.id} className="py-2" data-testid={`call-list-item-${l.id}`}>
                            <div className="flex items-center gap-2.5">
                            <span className="w-5 text-right font-mono-num text-xs text-gray-400 shrink-0">{i + 1}.</span>
                            <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-sm border shrink-0 ${gradeCls(si.grade)}`} title={(si.hints || []).join(" · ")}>
                                {si.grade} · {si.score}
                            </span>
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-bold text-[#111827] truncate flex items-center gap-1.5">
                                    <span className="truncate">{name}</span>
                                    {(l.bundled_count || 1) > 1 && (
                                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-800 border border-blue-200 shrink-0"
                                            title={`${l.bundled_count} Anfragen dieses Interessenten zusammengefasst:\n• ${(l.bundled_subjects || []).join("\n• ") || "weitere Anfragen"}`}
                                            data-testid={`call-list-bundled-${l.id}`}>
                                            ×{l.bundled_count} Anfragen
                                        </span>
                                    )}
                                </div>
                                <div className="text-[11px] text-gray-500 truncate">
                                    {l.subject && l.subject !== name ? l.subject : (l.reason || l.bank || "")}
                                </div>
                            </div>
                            {!isEmp && (
                                <select
                                    value={l.assigned_seller_id || ""}
                                    disabled={assigning === l.id}
                                    onChange={(e) => assignSeller(l, e.target.value)}
                                    className={`text-[11px] rounded-sm border px-1.5 py-1 max-w-[150px] shrink-0 focus:outline-none disabled:opacity-60 ${l.assigned_seller_id ? "border-emerald-300" : "border-amber-400 font-bold"}`}
                                    title={l.assigned_seller_name ? `Zugewiesen an ${l.assigned_seller_name} — hier ändern` : "Mitarbeiter zuweisen"}
                                    data-testid={`call-list-assign-${l.id}`}
                                >
                                    <option value="">— zuweisen —</option>
                                    {employees.map((emp) => (
                                        <option key={emp.id} value={emp.id}>{emp.name}{emp.absent ? " (abwesend)" : ""}</option>
                                    ))}
                                </select>
                            )}
                            {b && (
                                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm border shrink-0 ${b.cls}`}>{b.label}</span>
                            )}
                            {phone && !phoneWrong && (
                                <a href={`tel:${phone}`} onClick={() => setOutcomeFor(l.id)} className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-sm border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 shrink-0" data-testid={`call-list-call-${l.id}`}>
                                    <Phone className="w-3 h-3" /> Anrufen
                                </a>
                            )}
                            <Link to={`/dealer/leads?lead=${l.id}`} className="text-[11px] font-bold text-gray-500 hover:text-[#111827] shrink-0" data-testid={`call-list-open-${l.id}`}>
                                Öffnen
                            </Link>
                            </div>
                            {outcomeFor === l.id && (
                                <div className="mt-1.5 ml-7 flex flex-wrap items-center gap-1.5" data-testid={`call-list-outcome-${l.id}`}>
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Anruf-Ergebnis:</span>
                                    {OUTCOMES.map(([k, label]) => (
                                        <button key={k} type="button" onClick={() => saveOutcome(l, k)}
                                            className="text-[10px] font-bold px-2 py-1 rounded-sm border border-gray-300 bg-white hover:border-[#111827]"
                                            data-testid={`call-list-outcome-${l.id}-${k}`}>{label}</button>
                                    ))}
                                    <button type="button" onClick={() => setOutcomeFor("")}
                                        className="text-[10px] font-bold text-gray-400 hover:text-gray-700"
                                        data-testid={`call-list-outcome-later-${l.id}`}>Später</button>
                                </div>
                            )}
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}
